import { useCallback, useRef, useState } from 'react';
import FilterProvider from '@/context/filter/filter.context';
import { useDeleteClassMutation } from '@/features/class/class.mutations';
import { useClasses } from '@/features/class/class.queries';
import { toCapitalize } from '@/utils/text-utils';
import { Table } from '@tanstack/react-table';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { format, parseISO } from 'date-fns';
import { Plus, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { GetAllClassesResponseData } from '@/types/class.types';
import { useFilter } from '@/hooks/use-context/use-filters';

import { Button } from '@/components/ui/button';
import { ClassName } from '@/components/datatable/columns/classes/classes-columns';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import ClassDateFilter from '@/components/datatable/filters/class/class-date-filter';
import { DebouncedInput } from '@/components/debounced-input';
import AlertModal from '@/components/modals/alert-modal';
import ClassCreateUpdateSheet from '@/components/sheets/class-sheet/class-create-update-sheet';
import ClassSheet from '@/components/sheets/class-sheet/class-sheet';
import ContractViewSheet from '@/components/sheets/contract-sheet/contract-view-sheet';
import GradeCreateUpdateSheet from '@/components/sheets/grade-sheet/grade-create-update-sheet';
import StudentSheet from '@/components/sheets/student-sheet/student-sheet';

const classRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view']).optional(),
  class: z.number().optional(),
});

export const Route = createFileRoute('/_app/(classes)/classes/')({
  validateSearch: zodSearchValidator(classRouteSchema),
  component: Classes,
});

function Classes() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <ClassesTabHeader />
      <FilterProvider>
        <ClassesTable />
      </FilterProvider>
    </div>
  );
}

function ClassesTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['class']);

  const createSubject = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  const navigateToSettings = () => {
    navigate({ to: '/classes/settings' });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createSubject} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('class', { ns: 'common' }))}
        </Button>
        <Button className='flex items-center gap-3' onClick={navigateToSettings} size='default'>
          <Settings className='h-6 w-6' />
          {toCapitalize(t('settings', { ns: 'common' }))}
        </Button>
      </div>
      <ClassCreateUpdateSheet />
      <GradeCreateUpdateSheet />
    </div>
  );
}

function ClassesTable() {
  const { t } = useTranslation(['table', 'common']);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  const { filters } = useFilter();
  const { data = [], isLoading } = useClasses(filters.classDate);
  const { mutateAsync } = useDeleteClassMutation();
  const deletedId = useRef(0);

  const updateClass = (id: number) => {
    navigate({ to: '.', search: { mode: 'update', class: id } });
  };

  const onSuccessDelete = async () => {
    await mutateAsync(deletedId.current);
    deletedId.current = 0;
  };

  return (
    <>
      <DataTableSimple
        data={data}
        toolbar={ClassesToolbar}
        columns={[
          'id',
          'name',
          'category',
          'color',
          'subjectName',
          'teacherName',
          'numberOfContracts',
          'minContractDate',
          'maxContractDate',
          'information',
        ]}
        isLoading={isLoading}
        columnsLabels={{
          id: t('serial_id'),
          name: t('name'),
          color: t('color'),
          category: t('categories'),
          subjectName: t('subject'),
          teacherName: t('teacher'),
          numberOfContracts: t('number_of_contracts'),
          information: t('information'),
          minContractDate: t('minContractDate'),
          maxContractDate: t('maxContractDate'),
        }}
        customRender={{
          name: (row) => <ClassName className={row.getValue('name')} id={row.getValue('id')} />,
          minContractDate: (row) => (
            <ClassName
              className={format(parseISO(row.getValue('minContractDate')), 'dd/MM/yyyy')}
              id={row.getValue('id')}
            />
          ),
          maxContractDate: (row) => (
            <ClassName
              className={format(parseISO(row.getValue('maxContractDate')), 'dd/MM/yyyy')}
              id={row.getValue('id')}
            />
          ),
          color: (row) => (
            <div
              className='h-6 w-6 rounded-lg'
              style={{ backgroundColor: row.getValue('color') }}
            ></div>
          ),
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: t('edit', { ns: 'common' }),
                    onClick: () => updateClass(row.getValue('id')),
                    isLoading: false,
                  },
                  {
                    label: t('delete', { ns: 'common' }),
                    onClick: () => {
                      deletedId.current = row.getValue('id');
                      setDeleteModalOpen(true);
                    },
                  },
                ]}
              />
            ),
          },
        ]}
        className='flex h-full flex-col overflow-y-auto'
      />
      <ClassSheet />
      <ContractViewSheet />
      <StudentSheet />
      <AlertModal
        title={'Διαγραφή Τμήματος'}
        description={'Εισαι σίγουρος οτι θελεις να διαγραψεις το τμημα?'}
        setOpen={setDeleteModalOpen}
        isOpen={deleteModalOpen}
        onSuccess={onSuccessDelete}
      />
    </>
  );
}

function ClassesToolbar({ table }: { table: Table<GetAllClassesResponseData> }) {
  const { t } = useTranslation(['common']);
  const { setFilters } = useFilter();

  const applyFilters = useCallback(
    (f: Record<string, unknown>) => {
      table.setPageIndex(0);
      setFilters((prev) => ({ ...prev, ...f }));
    },
    [setFilters, table]
  );

  return (
    <div className='flex flex-1 items-center space-x-2 px-1'>
      <DebouncedInput
        value={table.getState().globalFilter ?? ''}
        onChange={(value) => table.setGlobalFilter(String(value))}
        className='h-8 w-[150px] lg:w-[250px]'
        placeholder={toCapitalize(t('search'))}
      />
      <ClassDateFilter applyFilters={applyFilters} />
    </div>
  );
}
